<template>
    <div class="stakeBox ticketVoucherSelect" v-bind:class="{ _disabled: !isSelectable }">
        <input
            class="ticketVoucherSelect-display"
            readonly
            v-bind:disabled="!isSelectable"
            v-bind:value="selectedOption?.label ?? ''"
            v-bind:placeholder="inputPlaceholder"
        />
        <select
            v-if="isSelectable"
            class="ticketVoucherSelect-native"
            v-bind:class="dropDownGaClass"
            v-bind:value="modelValue ?? ''"
            v-on:change="onChange"
        >
            <option
                v-for="option in displayOptions"
                v-bind:key="option.id"
                v-bind:value="option.id"
                v-bind:disabled="option.isDisabled"
            >
                {{ option.label }}
            </option>
        </select>
        <SboIcon
            v-if="!modelValue"
            class="ticketVoucherSelect-arrow"
            name="system:arrowDropDown"
            size="16"
        />
        <button
            v-if="isShowClearVoucher"
            class="stakeBox-clear"
            v-bind:class="clearVoucherGaClass"
            type="button"
            data-testid="voucher-clear"
            v-bind:disabled="disabled"
            v-on:click.stop="emit('update:modelValue', null)"
        >
            <SboIcon name="system:cross" size="16" />
        </button>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import { TicketVoucherOptionStatus } from '@/interface/enum';
    import type { ITicketVoucherOption } from '@/libraries/composables/useSingleModeVoucher';
    import { i18n } from '@/libraries/i18n';

    const props = defineProps<{
        options: ITicketVoucherOption[];
        modelValue: string | null;
        gaClassPrefix: string;
        disabled?: boolean;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: string | null];
    }>();

    const statusSuffixKeyMap: Partial<Record<TicketVoucherOptionStatus, string>> = {
        [TicketVoucherOptionStatus.SelectedByOtherTicket]: 'bet_slip_voucher_drop_down_selected',
        [TicketVoucherOptionStatus.UnavailableOddsInvalid]: 'bet_slip_voucher_drop_down_odds_not_eligible',
        [TicketVoucherOptionStatus.UnavailableStakeRange]: 'bet_slip_voucher_drop_down_stake_not_eligible',
    };

    const selectableStatuses: Set<TicketVoucherOptionStatus> = new Set([
        TicketVoucherOptionStatus.Available,
        TicketVoucherOptionStatus.Selected,
    ]);

    const displayOptions = computed(() => props.options.map((option) => {
        const suffixKey = statusSuffixKeyMap[option.status] ?? '';
        return {
            id: option.id,
            label: !suffixKey ? option.text : `${option.text} | ${i18n.t(suffixKey)}`,
            isDisabled: !selectableStatuses.has(option.status),
        };
    }));

    const hasNoVoucherOption = computed(() => props.options.length === 0);

    const isSelectable = computed(() => !hasNoVoucherOption.value && props.disabled !== true);

    const inputPlaceholder = computed(() => (
        hasNoVoucherOption.value
            ? i18n.t('bet_slip_voucher_no_voucher')
            : i18n.t('bet_slip_voucher_select_voucher')
    ));

    const isShowClearVoucher = computed(() => !hasNoVoucherOption.value && props.modelValue !== null);

    const selectedOption = computed(() => displayOptions.value.find(o => o.id === props.modelValue) ?? null);

    const dropDownGaClass = computed(() => `${props.gaClassPrefix}_Voucher_DropDown`);

    const clearVoucherGaClass = computed(() => `${props.gaClassPrefix}_Btn_ClearVoucher`);

    function onChange(event: Event) {
        if (!(event.target instanceof HTMLSelectElement)) {
            return;
        }
        emit('update:modelValue', event.target.value || null);
    }
</script>

<style lang="scss" scoped>
@import "@/scss/components/singleMode/ticketVoucherSelect";
</style>
